import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Card } from 'primereact/card'

import { PageLayoutContainer } from '../../../lib/page-layout-container'
import {
    PaymentGetSubscriptionsByOrgApiArg,
    PaymentOrganizationSubscriptionRequest,
    PaymentOrganizationSubscriptionResponse,
    usePaymentCreateOrganizationSubscriptionMutation,
    usePaymentCreateSelfServicePortalMutation,
    useCompanyGetCompanySettingsQuery,
    useCompanyUpdateCompanySettingsMutation,
    usePaymentGetBillableConnectorsQuery,
    usePaymentGetCommercialOfferingsQuery,
    usePaymentGetCustomerQuery,
    usePaymentGetSubscriptionDetailsQuery,
    usePaymentGetSubscriptionsByOrgQuery,
} from '../../../common/redux/api/web-api'
import { RootState } from '../../redux/store'
import { getFailureReasonFromError } from '../../../common/services/utilsService'
import { BillingPaymentDetailsWidget } from '../../components/billing-payment-details-widget/billing-payment-details-widget'
import { useToast } from '../../../common/uikit/ToastProvider'
import useText from '../../../common/hooks/useText'
import { BillingPlanInterval, BillingPlanWidget } from '../../components/billing-plan-widget/billing-plan-widget'
import { BillingSubscriptionsWidget } from '../../components/billing-subscriptions-widget/billing-subscriptions-widget'
import {
    DEFAULT_BILLING_COUNTRY,
    getCompanySettingValue,
    mapOrganisationSettingsToCompanySettings,
} from '../../../common/utils/company-settings'

import {
    CompleteOrganisationInformationDialog,
    type CompleteOrganisationInformationValues,
} from './dialogs/complete-organisation-information-dialog'

export type BillingPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

const ACTIVE_SUBSCRIPTIONS_QUERY: PaymentGetSubscriptionsByOrgApiArg = { status: ['active', 'past_due'] }

const toBillingPlanInterval = (interval?: string | null): BillingPlanInterval | undefined =>
    interval === 'month' || interval === 'year' ? interval : undefined

export const BillingPage = memo(function BillingPage({ className, style }: BillingPageProps): React.ReactElement {
    const navigate = useNavigate()
    const toast = useToast()
    const text = useText()
    const [dismissedOrganisationDataCompanyId, setDismissedOrganisationDataCompanyId] = useState<string | null>(null)

    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const {
        data: companySettings,
        isFetching: isFetchingCompanySettings,
        isSuccess: isCompanySettingsLoaded,
    } = useCompanyGetCompanySettingsQuery({ companyId }, { skip: !companyId })

    const { data: customer, isLoading: isLoadingCustomer } = usePaymentGetCustomerQuery(
        companyId ? { organizationId: companyId } : skipToken
    )
    const { data: subscriptions, isLoading: isLoadingSubscriptions } = usePaymentGetSubscriptionsByOrgQuery(
        ACTIVE_SUBSCRIPTIONS_QUERY,
        { refetchOnMountOrArgChange: true }
    )

    const activeSubscription: PaymentOrganizationSubscriptionResponse | null = subscriptions?.[0] ?? null
    const hasActiveSubscription = activeSubscription !== null
    const subscriptionDetailsQueryArg =
        customer?.publicId && activeSubscription?.publicId
            ? {
                  customerPublicId: customer.publicId,
                  organizationId: companyId,
                  subscriptionPublicId: activeSubscription.publicId,
              }
            : undefined
    const { data: subscriptionDetails, isLoading: isLoadingSubscriptionDetails } =
        usePaymentGetSubscriptionDetailsQuery(subscriptionDetailsQueryArg ?? skipToken)
    const billingPlanInterval = toBillingPlanInterval(subscriptionDetails?.items?.[0]?.interval)

    const [createCustomerPortal] = usePaymentCreateSelfServicePortalMutation()

    const { data: commercialOfferings, isLoading: isLoadingCommercialOfferings } =
        usePaymentGetCommercialOfferingsQuery(
            { kind: 'subscription' },
            { skip: isLoadingSubscriptions || hasActiveSubscription }
        )

    const { data: billableConnectors, isLoading: isLoadingBillableConnectors } = usePaymentGetBillableConnectorsQuery(
        companyId ? { organizationId: companyId } : skipToken
    )
    const activeSubscriptionBillableConnectors =
        billableConnectors?.quantities?.find((q) => q?.strategyKey === activeSubscription?.quantityStrategyKey)
            ?.quantity ?? 0

    const [createOrganizationSubscription] = usePaymentCreateOrganizationSubscriptionMutation()
    const [updateCompanySettings, { isLoading: isSavingCompanySettings }] = useCompanyUpdateCompanySettingsMutation()
    const communicationLanguage = getCompanySettingValue(companySettings, 'Language')
    const configuredBillingCountry = getCompanySettingValue(companySettings, 'BillingCountry')
    const billingCountry = configuredBillingCountry ?? DEFAULT_BILLING_COUNTRY
    const isOrganisationDataLoading = isFetchingCompanySettings
    const isOrganisationDataLoaded = isCompanySettingsLoaded
    const shouldPromptForOrganisationData =
        isOrganisationDataLoaded && !isOrganisationDataLoading && (!configuredBillingCountry || !communicationLanguage)
    const showOrganisationDataPrompt =
        shouldPromptForOrganisationData && dismissedOrganisationDataCompanyId !== companyId
    const isSavingOrganisationData = isSavingCompanySettings

    const handleCreateOrganizationSubscription = async (request: PaymentOrganizationSubscriptionRequest) => {
        try {
            await createOrganizationSubscription({ paymentOrganizationSubscriptionRequest: request }).unwrap()
            navigate('/billing/success', { replace: true })
        } catch (err) {
            const reason = getFailureReasonFromError(err)
            const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
            navigate(`/billing/failed${query}`, { replace: true })
        }
    }

    const handleManagePayments = async () => {
        if (!companyId) return

        try {
            const { url } = await createCustomerPortal({
                customerId: companyId,
                paymentSelfServicePortalRequest: { idempotencyKey: crypto.randomUUID() },
            }).unwrap()

            window.open(url, '_blank', 'noopener,noreferrer')
        } catch {
            toast.notifyError(text('lblErrorSetupCustomerPortal'))
        }
    }

    const handleSubmitOrganisationInformation = async ({
        billingCountry: nextBillingCountry,
        communicationLanguage,
    }: CompleteOrganisationInformationValues) => {
        try {
            await updateCompanySettings({
                companyId,
                body: mapOrganisationSettingsToCompanySettings({
                    communicationLanguage,
                    billingCountry: nextBillingCountry,
                }),
            }).unwrap()

            setDismissedOrganisationDataCompanyId(companyId)
            toast.notifySuccess(text('msgCompanyUpdated'))
        } catch {
            toast.notifyError(text('msgCompanyNotUpdated'))
        }
    }

    const isLoadingContent =
        isLoadingBillableConnectors ||
        isLoadingCommercialOfferings ||
        isLoadingSubscriptions ||
        isLoadingCustomer ||
        (hasActiveSubscription && isLoadingSubscriptionDetails)

    const renderSubscriptionContent = () => {
        if (hasActiveSubscription) {
            return <BillingPlanWidget interval={billingPlanInterval} subscription={activeSubscription} />
        }

        if (commercialOfferings) {
            return (
                <BillingSubscriptionsWidget
                    companyId={companyId}
                    communicationLanguage={communicationLanguage}
                    isCommunicationLanguageLoading={isFetchingCompanySettings}
                    commercialOfferings={commercialOfferings}
                    billableConnectors={billableConnectors}
                    onCreateOrganizationSubscription={handleCreateOrganizationSubscription}
                />
            )
        }

        return (
            <Card>
                <p className="m-0 text-xl">{text('lblCouldLoadCommercialOfferings')}</p>
            </Card>
        )
    }

    return (
        <PageLayoutContainer className={cn(className, 'gap-3')} style={style}>
            <CompleteOrganisationInformationDialog
                visible={showOrganisationDataPrompt}
                initialValues={{ billingCountry, communicationLanguage: communicationLanguage ?? '' }}
                isSaving={isSavingOrganisationData}
                onHide={() => setDismissedOrganisationDataCompanyId(companyId)}
                onSubmit={handleSubmitOrganisationInformation}
            />

            {!isLoadingContent ? (
                <>
                    {renderSubscriptionContent()}

                    <BillingPaymentDetailsWidget
                        billableConnectors={activeSubscriptionBillableConnectors}
                        hasActiveSubscription={hasActiveSubscription}
                        isCustomer={!!customer}
                        onManagePayments={handleManagePayments}
                    />
                </>
            ) : (
                <ProgressSpinner />
            )}
        </PageLayoutContainer>
    )
})
